import React, { useState, useEffect } from 'react'

import { Search } from 'lucide-react'

import { Group } from '@/core/api/entities/group'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useGroups } from '@/core/hooks/useGroup'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { ModalPaginator } from '@/shared/components/molecules/ModalPaginator/ModalPaginator'
import { cn } from '@/shared/utils/helpers'

interface AddGroupToZoneProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onConfirm: (selectedGroupIds: number[], initialGroupIds: number[]) => void
	isAssigning: boolean
}

const AddGroupToZone: React.FC<AddGroupToZoneProps> = ({
	visible,
	zone,
	onClose,
	onConfirm,
	isAssigning,
}) => {
	const { t } = useTranslation('common')
	const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
	const [initialGroupIds, setInitialGroupIds] = useState<number[]>([])
	const [hasUserChangedSelection, setHasUserChangedSelection] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [page, setPage] = useState(1)

	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	useEffect(() => {
		setPage(1)
	}, [debouncedSearchQuery])

	useEffect(() => {
		if (visible) {
			setHasUserChangedSelection(false)
		}
	}, [visible])

	// Fetch groups with server-side pagination and search
	const { data: groupsResponse, isLoading: isLoadingGroups } = useGroups({
		config: useAccountConfig({
			params: {
				page,
				per_page: 20,
				query: debouncedSearchQuery || undefined,
				include: 'players',
			},
		}),
		options: {
			enabled: visible,
		},
	})

	const groups = groupsResponse?.data || []
	const totalPages = groupsResponse?.meta?.pagination?.total_pages ?? 1

	// Pre-select groups that the zone already belongs to
	useEffect(() => {
		if (zone?.groups && visible && !hasUserChangedSelection) {
			const currentGroupIds = zone.groups.map((g) => g.id)
			setSelectedGroupIds(currentGroupIds)
			setInitialGroupIds(currentGroupIds)
		}
	}, [zone, visible, hasUserChangedSelection])

	// Handle group selection toggle
	const handleGroupToggle = (groupId: number, checked: boolean) => {
		setHasUserChangedSelection(true)
		if (checked) {
			setSelectedGroupIds((prevSelectedGroupIds) => [
				...prevSelectedGroupIds,
				groupId,
			])
		} else {
			setSelectedGroupIds((prevSelectedGroupIds) =>
				prevSelectedGroupIds.filter((id) => id !== groupId)
			)
		}
	}

	// Handle confirm
	const handleConfirm = () => {
		onConfirm(selectedGroupIds, initialGroupIds)
	}

	// Handle close
	const handleClose = () => {
		if (isAssigning) return // Prevent closing while assigning
		onClose()
		setSelectedGroupIds([])
		setInitialGroupIds([])
		setHasUserChangedSelection(false)
		setSearchQuery('')
		setPage(1)
	}

	// Calculate selected/available counts for display
	const selectedCount = selectedGroupIds.length

	return (
		<Dialog
			open={visible}
			onOpenChange={(open) => {
				if (!open) handleClose()
			}}
		>
			<DialogContent className="max-w-3xl p-0 gap-0 flex flex-col max-h-[90vh]">
				<DialogHeader className="px-6 pt-5 pb-3">
					<DialogTitle className="text-xl">
						{t('entities.zone.assign_to_group.title')}
					</DialogTitle>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
					<div className="relative flex-none mb-3">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder={t(
								'entities.zone.assign_to_group.search_placeholder'
							)}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-10"
						/>
					</div>

					<div className="flex items-center justify-between flex-none mb-3">
						<span className="text-sm text-muted-foreground">
							{t(
								selectedCount === 1
									? 'entities.zone.assign_to_group.selected_one'
									: 'entities.zone.assign_to_group.selected_other',
								{ count: selectedCount }
							)}
						</span>
					</div>

					{/* Groups List */}
					<ScrollArea className="h-[calc(100dvh-300px)] sm:h-[calc(90vh-300px)] border rounded-md">
						{isLoadingGroups ? (
							<div className="flex items-center justify-center h-full text-muted-foreground text-sm pt-3">
								{t('entities.zone.assign_to_group.loading')}
							</div>
						) : groups.length === 0 ? (
							<div className="flex items-center justify-center h-full text-muted-foreground text-sm pt-3">
								{debouncedSearchQuery
									? t('entities.zone.assign_to_group.no_results')
									: t('entities.zone.assign_to_group.empty')}
							</div>
						) : (
							<div className="divide-y">
								{groups.map((group: Group) => {
									const isSelected = selectedGroupIds.includes(group.id)
									const zoneCount = group.zones?.length || 0
									const zoneText = t(
										zoneCount === 1
											? 'entities.zone.assign_to_group.zone_count_one'
											: 'entities.zone.assign_to_group.zone_count_other',
										{ count: zoneCount }
									)

									return (
										<div
											key={`group-${group.id}`}
											className={cn(
												'flex items-center justify-between px-4 py-4 hover:bg-shade cursor-pointer',
												isSelected ? 'bg-shade' : ''
											)}
											onClick={() => handleGroupToggle(group.id, !isSelected)}
										>
											<div className="flex-1 pr-4 flex flex-col justify-center">
												<p className="text-sm font-medium mb-1">{group.name}</p>
												<p className="text-xs text-muted-foreground">
													{zoneText}
												</p>
											</div>
											<Checkbox
												checked={isSelected}
												onCheckedChange={(checked) =>
													handleGroupToggle(group.id, checked as boolean)
												}
												onClick={(e) => e.stopPropagation()}
											/>
										</div>
									)
								})}
							</div>
						)}
					</ScrollArea>
					<ModalPaginator
						currentPage={page}
						totalPages={totalPages}
						onPageChange={setPage}
					/>
				</div>

				<Separator />

				{/* Footer Buttons */}
				<DialogFooter className="px-6 py-4">
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={isAssigning}
						className="h-9 rounded-full px-6"
					>
						{t('actions.cancel')}
					</Button>
					<Button
						type="button"
						onClick={handleConfirm}
						disabled={isAssigning || selectedGroupIds.length === 0}
						className="h-9 rounded-full px-6"
					>
						{isAssigning
							? t('actions.loading')
							: t(
									selectedCount === 1
										? 'entities.zone.assign_to_group.confirm_one'
										: 'entities.zone.assign_to_group.confirm_other',
									{ count: selectedCount }
								)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default AddGroupToZone
